/**
 * Localization strings used in the Tree Viewer
 *
 * @module
 */

import { createDictionary } from './utils';

// Refer to "Guidelines for Programmers" in ./README.md before editing this file

export const treeText = createDictionary({
  trees: {
    'en-us': 'Trees',
    'ru-ru': 'Деревья',
    'es-es': 'Árboles',
    'fr-fr': 'Arbres',
    'uk-ua': 'дерева',
    'de-ch': 'Hierarchien',
    'pt-br': 'Árvores',
  },
  badStructure: {
    'en-us': 'Bad tree structure.',
    'ru-ru': 'Неправильная структура дерева.',
    'es-es': 'Estructura de árbol incorrecta.',
    'fr-fr': 'Mauvaise arborescence.',
    'uk-ua': 'Погана структура дерева.',
    'de-ch': 'Fehlerhafte Baumstruktur.',
    'pt-br': 'Má estrutura da árvore.',
  },
  move: {
    'en-us': 'Move',
    'ru-ru': 'Двигаться',
    'es-es': 'Mover',
    'fr-fr': 'Déplacer',
    'uk-ua': 'рухатися',
    'de-ch': 'Verschieben',
    'pt-br': 'Mover',
  },
  merge: {
    'en-us': 'Merge',
    'ru-ru': 'Слияние',
    'es-es': 'Unir',
    'fr-fr': 'Fusionner',
    'uk-ua': 'Об’єднати',
    'de-ch': 'Zusammenführen',
    'pt-br': 'Fusão',
  },
  undoSynonymy: {
    'en-us': 'Undo Synonymy',
    'ru-ru': 'Отменить синонимию',
    'es-es': 'Deshacer sinonimia',
    'fr-fr': 'Annuler la synonymie',
    'uk-ua': 'Скасувати синонімію',
    'de-ch': 'Synonymie rückgängig machen',
    'pt-br': 'Desfazer Sinônimo',
  },
  synonymize: {
    'en-us': 'Synonymize',
    'ru-ru': 'Подбирать синонимы',
    'es-es': 'Sinonimizar',
    'fr-fr': 'Synonymiser',
    'uk-ua': 'Синонімізувати',
    'de-ch': 'Synonymisieren',
    'pt-br': 'Sinônimos',
  },
  actionFailed: {
    'en-us': 'Operation failed',
    'ru-ru': 'Операция провалилась',
    'es-es': 'Operación fallida',
    'fr-fr': "L'opération a échoué",
    'uk-ua': 'Операція не вдалася',
    'de-ch': 'Vorgang fehlgeschlagen',
    'pt-br': 'Operação falhou',
  },
  actionFailedDescription: {
    'en-us':
      'The operation could not be completed due to the following errors:',
    'ru-ru': 'Операция не может быть завершена из-за следующих ошибок:',
    'es-es':
      'La operación no se pudo completar debido a los siguientes errores:',
    'fr-fr':
      "L'opération n'a pas pu être terminée en raison des erreurs suivantes :",
    'de-ch':
      'Der Vorgang konnte aufgrund der folgenden Fehler nicht ausgeführt werden:',
    'uk-ua': 'Операцію не вдалося завершити через такі помилки:',
    'pt-br': 'A operação não pôde ser concluída devido aos seguintes erros:',
  },
  moveNode: {
    'en-us': 'Move node',
    'ru-ru': 'Переместить узел',
    'es-es': 'Mover nodo',
    'fr-fr': 'Déplacer le nœud',
    'uk-ua': 'Перемістити вузол',
    'de-ch': 'Knoten verschieben',
    'pt-br': 'Mover nó',
  },
  addChild: {
    'en-us': 'Add Child',
    'ru-ru': 'Добавить ребенка',
    'es-es': 'Agregar hijo',
    'fr-fr': 'Ajouter un enfant',
    'uk-ua': 'Додати дитину',
    'de-ch': 'Kind hinzuzufügen',
    'pt-br': 'Adicionar filho',
  },
  moveNodeHere: {
    'en-us': 'Move "{nodeName:string}" here',
    'ru-ru': 'Переместите "{nodeName:string}" сюда',
    'es-es': 'Mover "{nodeName:string}" aquí',
    'fr-fr': 'Déplacer « {nodeName:string} » ici',
    'uk-ua': 'Перемістіть сюди "{nodeName:string}"',
    'de-ch': 'Verschiebe "{nodeName:string}" hierhin',
    'pt-br': 'Mova "{nodeName:string}" para cá',
  },
  moveNodePreparationsHere: {
    'en-us': 'Move all "{nodeName:string}" preparations here',
    'de-ch':
      'Verschieben Sie alle Vorbereitungen mit dem Präfix "{nodeName:string}" hierher.',
    'es-es': 'Mover todas las preparaciones "{nodeName:string}" aquí',
    'fr-fr': 'Déplacer toutes les préparations « {nodeName:string} » ici',
    'ru-ru': 'Переместите сюда все приготовления "{nodeName:string}".',
    'uk-ua': 'Перемістіть сюди всі препарати "{nodeName:string}"',
    'pt-br': 'Mova todos os preparativos "{nodeName:string}" para cá.',
  },
  nodeMoveMessage: {
    'en-us':
      'The {treeName:string} node "{nodeName:string}" will be placed, along with all of its descendants, under the new parent "{parentName:string}".',
    'ru-ru':
      'Узел {treeName:string} "{nodeName:string}" будет помещен вместе со всеми его потомками под новый родительский узел "{parentName:string}".',
    'es-es':
      'El nodo {treeName:string} "{nodeName:string}" se colocará, junto con todos sus descendientes, bajo el nuevo padre "{parentName:string}".',
    'fr-fr':
      'Le nœud {treeName:string} « {nodeName:string} » sera placé, avec tous ses descendants, sous le nouveau parent « {parentName:string} ».',
    'uk-ua':
      'Вузол {treeName:string} "{nodeName:string}" буде розміщено разом із усіма його нащадками під новим батьківським вузлом "{parentName:string}".',
    'de-ch':
      'Der {treeName:string} Knoten "{nodeName:string}" wird zusammen mit allen seinen Unterknoten unter den neuen übergeordneten Knoten "{parentName:string}" platziert.',
    'pt-br':
      'O nó {treeName:string} "{nodeName:string}" será colocado, juntamente com todos os seus descendentes, sob o novo pai "{parentName:string}".',
  },
  nodeBulkMoveMessage: {
    'en-us':
      'The {treeName:string} node "{nodeName:string}" preparations will be placed under the new location "{parentName:string}".',
    'de-ch':
      'Die Vorbereitungen für den Knoten {treeName:string} "{nodeName:string}" werden unter dem neuen Standort "{parentName:string}" zusammengefasst.',
    'es-es':
      'Las preparaciones del nodo {treeName:string} "{nodeName:string}" se colocarán en la nueva ubicación "{parentName:string}".',
    'fr-fr':
      'Les préparatifs du nœud {treeName:string} « {nodeName:string} » seront placés sous le nouvel emplacement « {parentName:string} ».',
    'ru-ru':
      'Подготовка узла {treeName:string} "{nodeName:string}" будет размещена в новом месте "{parentName:string}".',
    'uk-ua':
      'Підготовка вузла {treeName:string} "{nodeName:string}" буде розміщена в новому місці розташування "{parentName:string}".',
    'pt-br':
      'Os preparativos do nó {treeName:string} "{nodeName:string}" serão colocados no novo local "{parentName:string}".',
  },
  cantMoveHere: {
    'en-us': "Can't move this tree node here",
    'ru-ru': 'Невозможно переместить этот узел дерева сюда.',
    'es-es': 'No se puede mover este nodo del árbol aquí',
    'fr-fr': "Impossible de déplacer ce nœud d'arbre ici",
    'uk-ua': 'Неможливо перемістити цей вузол дерева сюди',
    'de-ch': 'Dieser Knoten kann nicht hierhin verschoben werden',
    'pt-br': 'Não é possível mover este nó da árvore para cá.',
  },
  cantMergeHere: {
    'en-us': "Can't merge this tree node here",
    'ru-ru': 'Невозможно объединить этот узел дерева.',
    'es-es': 'No se puede fusionar este nodo de árbol aquí',
    'fr-fr': "Impossible de fusionner ce nœud d'arbre ici",
    'uk-ua': 'Неможливо об’єднати цей вузол дерева тут',
    'de-ch': 'Dieser Knoten kann hier nicht zusammengelegt werden',
    'pt-br': 'Não é possível mesclar este nó da árvore aqui.',
  },
  cantMoveToSynonym: {
    'en-us': "Can't move to a synonym",
    'ru-ru': 'Невозможно перейти к синониму',
    'es-es': 'No se puede mover a un sinónimo',
    'fr-fr': 'Impossible de passer à un synonyme',
    'uk-ua': 'Неможливо перейти до синоніма',
    'de-ch': 'Kann nicht zu einem Synonym verschieben',
    'pt-br': 'Não é possível mover para um sinônimo',
  },
  cantMergeIntoSynonym: {
    'en-us': "Can't merge into synonyms",
    'ru-ru': 'Невозможно объединить в синонимы.',
    'es-es': 'No se pueden fusionar en sinónimos',
    'fr-fr': 'Impossible de fusionner en synonymes',
    'uk-ua': 'Не можна об’єднувати в синоніми',
    'de-ch': 'Kann nicht zu Synonymen zusammenführen',
    'pt-br': 'Não é possível mesclar em sinônimos.',
  },
  cantSynonymizeSynonym: {
    'en-us': "Can't synonymize with a synonym",
    'ru-ru': 'Нельзя использовать синоним для обозначения чего-либо.',
    'es-es': 'No se puede sinonimizar con un sinónimo',
    'fr-fr': 'Impossible de synonymiser avec un synonyme',
    'uk-ua': 'Не можна синонімізувати синонім',
    'de-ch': 'Kann nicht mit einem Synonym synonymisiert werden',
    'pt-br': 'Não é possível usar um sinônimo com outro sinônimo.',
  },
  nodeMoveHintMessage: {
    'en-us': 'Select a new parent for "{nodeName:string}"',
    'ru-ru': 'Выберите нового родителя для "{nodeName:string}"',
    'es-es': 'Seleccione un nuevo padre para "{nodeName:string}"',
    'fr-fr': 'Sélectionnez un nouveau parent pour « {nodeName:string} »',
    'uk-ua': 'Виберіть новий батьківський елемент для "{nodeName:string}"',
    'de-ch': 'Wählen Sie ein neues Elternelement für "{nodeName:string}"',
    'pt-br': 'Selecione um novo pai para "{nodeName:string}"',
  },
  mergeNode: {
    'en-us': 'Merge node',
    'ru-ru': 'Слияние узлов',
    'es-es': 'Fusionar el nodo',
    'fr-fr': 'Fusionner le nœud',
    'uk-ua': 'Вузол злиття',
    'de-ch': 'Knoten zusammenführen',
    'pt-br': 'Nó de mesclagem',
  },
  mergeNodeHere: {
    'en-us': 'Merge "{nodeName:string}" here',
    'ru-ru': 'Объедините "{nodeName:string}" здесь',
    'es-es': 'Fusionar "{nodeName:string}" aquí',
    'fr-fr': 'Fusionner « {nodeName:string} » ici',
    'uk-ua': 'Об\'єднайте "{nodeName:string}" тут',
    'de-ch': 'Führe "{nodeName:string}" hier zusammen',
    'pt-br': 'Mescle "{nodeName:string}" aqui',
  },
  mergeNodeHintMessage: {
    'en-us': 'Select a new target for "{nodeName:string}" to be merged into',
    'ru-ru': 'Выберите новый целевой объект для слияния "{nodeName:string}".',
    'es-es': 'Seleccione un nuevo objetivo para fusionar "{nodeName:string}"',
    'fr-fr':
      'Sélectionnez une nouvelle cible pour que « {nodeName:string} » soit fusionné dans',
    'uk-ua':
      'Виберіть нову ціль для «{nodeName:string}», у яку потрібно об’єднати',
    'de-ch': 'Wähle ein neues Ziel um "{nodeName:string}" zusammenzuführen',
    'pt-br':
      'Selecione um novo destino para "{nodeName:string}" a ser mesclado.',
  },
  bulkMoveNodeHintMessage: {
    'en-us':
      'Select a new target for "{nodeName:string}" preparations to be moved into',
    'de-ch':
      'Wählen Sie ein neues Ziel für die "{nodeName:string}"-Vorbereitungen aus, die verschoben werden sollen.',
    'es-es':
      'Seleccione un nuevo objetivo para los preparaciones "{nodeName:string}" que se trasladarán a',
    'fr-fr':
      'Sélectionnez une nouvelle cible pour le déplacement des préparations « {nodeName:string} » dans',
    'ru-ru':
      'Выберите новую цель для перемещения подготовительных работ "{nodeName:string}".',
    'uk-ua':
      'Виберіть нову ціль для препаратів "{nodeName:string}", до якої потрібно переміститися',
    'pt-br':
      'Selecione um novo alvo para os preparativos "{nodeName:string}" a serem movidos para',
  },
  mergeNodeMessage: {
    'en-us':
      'All references to {treeName:string} node "{nodeName:string}" will be replaced with "{parentName:string}", and all descendants of "{nodeName:string}" will be moved to "{parentName:string}" with any descendants matching in name and rank being themselves merged recursively.',
    'ru-ru':
      'Все ссылки на узел {treeName:string} "{nodeName:string}" будут заменены на "{parentName:string}", а все потомки "{nodeName:string}" будут перемещены в "{parentName:string}", при этом любые потомки, совпадающие по имени и рангу, будут рекурсивно объединены.',
    'es-es':
      'Todas las referencias al nodo {treeName:string} "{nodeName:string}" serán reemplazadas por "{parentName:string}", todos los descendientes de "{nodeName:string}" se moverán a "{parentName:string}" y todos los descendientes que coincidan en nombre y rango se fusionarán de forma recursiva.',
    'fr-fr':
      'Toutes les références au nœud {treeName:string} "{nodeName:string}" seront remplacées par "{parentName:string}", et tous les descendants de "{nodeName:string}" seront déplacés vers "{parentName:string}" avec tous les descendants correspondant en nom et en rang étant eux-mêmes fusionnés de manière récursive.',
    'uk-ua':
      "Усі посилання на вузол {treeName:string} «{nodeName:string}» буде замінено на «{parentName:string}», а всі нащадки «{nodeName:string}» буде переміщено до «{parentName:string}», а будь-які нащадки, що відповідають імені та рангу, будуть самі собою об'єднані рекурсивно.",
    'de-ch':
      'Alle Referenzen zu {treeName:string} "{nodeName:string}" werden mit "{parentName:string}" ersetzt. Alle Nachkommen von "{nodeName:string}" werden nach "{parentName:string}" verschoben, wobei alle Nachkommen, die in Name und Rang übereinstimmen, selbst rekursiv zusammengeführt werden.',
    'pt-br':
      'Todas as referências ao nó {treeName:string} "{nodeName:string}" serão substituídas por "{parentName:string}", e todos os descendentes de "{nodeName:string}" serão movidos para "{parentName:string}", com quaisquer descendentes correspondentes em nome e classificação sendo eles próprios mesclados recursivamente.',
  },
  synonymizeNode: {
    'en-us': 'Synonymize node',
    'ru-ru': 'Синонимизировать узел',
    'es-es': 'Sinonimizar nodo',
    'fr-fr': 'Synonymiser le nœud',
    'uk-ua': 'Синонімізувати вузол',
    'de-ch': 'Knoten synonymisieren',
    'pt-br': 'Sinônimo de nó',
  },
  makeSynonym: {
    'en-us': 'Make {nodeName:string} a synonym of {synonymName:string}',
    'ru-ru': 'Сделайте {nodeName:string} синонимом {synonymName:string}',
    'es-es': 'Hacer de {nodeName:string} un sinónimo de {synonymName:string}',
    'fr-fr': 'Faire de {nodeName:string} un synonyme de {synonymName:string}',
    'uk-ua': 'Зробити {nodeName:string} синонімом {synonymName:string}',
    'de-ch':
      'Aus {nodeName:string} ein Synonym von {synonymName:string} machen',
    'pt-br': 'Faça de {nodeName:string} um sinônimo de {synonymName:string}',
  },
  synonymizeNodeHintMessage: {
    'en-us': 'Select a target for "{nodeName:string}" to be synonymized to',
    'ru-ru':
      'Выберите целевой объект для "{nodeName:string}", который будет синонимизирован.',
    'es-es':
      'Seleccione un objetivo para que "{nodeName:string}" se convierta en sinónimo',
    'fr-fr':
      'Sélectionnez une cible pour que « {nodeName:string} » soit synonymisé à',
    'uk-ua': 'Виберіть ціль для "{nodeName:string}", який буде синонімічним',
    'de-ch': 'Wähle Ziel um "{nodeName:string}" daran zu synonymisieren',
    'pt-br': 'Selecione um alvo para "{nodeName:string}" a ser sinônimo de',
  },
  synonymizeMessage: {
    'en-us':
      'The {treeName:string} node "{nodeName:string}" will be made a synonym of "{synonymName:string}".',
    'ru-ru':
      'Узел {treeName:string} "{nodeName:string}" будет синонимом "{synonymName:string}".',
    'es-es':
      'El nodo {treeName:string} "{nodeName:string}" se convertirá en sinónimo de "{synonymName:string}".',
    'fr-fr':
      'Le nœud {treeName:string} «{nodeName:string}» sera rendu synonyme de «{synonymName:string}».',
    'de-ch':
      'Der {treeName:string}-Knoten "{nodeName:string}" wird zu einem Synonym von "{synonymName:string}".',
    'uk-ua':
      'Вузол {treeName:string} "{nodeName:string}" стане синонімом "{synonymName:string}".',
    'pt-br':
      'O nó {treeName:string} "{nodeName:string}" será transformado em sinônimo de "{synonymName:string}".',
  },
  desynonymizeNode: {
    'en-us': 'Desynonymize node',
    'ru-ru': 'Десинонимизировать узел',
    'es-es': 'Desinonimizar nodo',
    'fr-fr': 'Désynonymiser le nœud',
    'uk-ua': 'Десинонімізувати вузол',
    'de-ch': 'Knoten desynonymisieren',
    'pt-br': 'Dessinonizar nó',
  },
  desynonymizeNodeMessage: {
    'en-us':
      '"{nodeName:string}" will no longer be a synonym of "{synonymName:string}".',
    'ru-ru':
      '"{nodeName:string}" больше не будет синонимом "{synonymName:string}".',
    'es-es':
      '"{nodeName:string}" ya no será sinónimo de "{synonymName:string}".',
    'fr-fr':
      '« {nodeName:string} » ne sera plus synonyme de « {synonymName:string} ».',
    'de-ch':
      '"{nodeName:string}" wird nicht mehr ein Synonym von "{synonymName:string}" sein.',
    'uk-ua':
      '"{nodeName:string}" більше не буде синонімом "{synonymName:string}".',
    'pt-br':
      '"{nodeName:string}" deixará de ser sinônimo de "{synonymName:string}".',
  },
  acceptedName: {
    'en-us': 'Preferred: {name:string}',
    'ru-ru': 'Предпочтительный вариант: {name:string}',
    'es-es': 'Preferido: {name:string}',
    'fr-fr': 'Préféré : {name:string}',
    'uk-ua': 'Бажано: {name:string}',
    'de-ch': 'Bevorzugt: {name:string}',
    'pt-br': 'Preferencial: {name:string}',
  },
  synonyms: {
    'en-us': 'Synonyms: {names:string}',
    'de-ch': 'Synonyme: {names:string}',
    'es-es': 'Sinónimos: {names:string}',
    'fr-fr': 'Synonymes : {names:string}',
    'ru-ru': 'Синонимы: {names:string}',
    'uk-ua': 'Синоніми: {names:string}',
    'pt-br': 'Sinônimos: {names:string}',
  },
  treeViewTitle: {
    'en-us': '{treeName:string} Tree',
    'ru-ru': '{treeName:string} Дерево',
    'es-es': '{treeName:string} Árbol',
    'fr-fr': '{treeName:string} Arbre',
    'uk-ua': '{treeName:string} Дерево',
    'de-ch': '{treeName:string} Baum',
    'pt-br': '{treeName:string} Árvore',
  },
  searchTreePlaceholder: {
    'en-us': 'Search Tree',
    'ru-ru': 'Дерево поиска',
    'es-es': 'Árbol de búsqueda',
    'fr-fr': "Rechercher dans l'arbre",
    'uk-ua': 'Дерево пошуку',
    'de-ch': 'Baum durchsuchen',
    'pt-br': 'Árvore de pesquisa',
  },
  opened: {
    'en-us': 'Opened',
    'ru-ru': 'Открыто',
    'es-es': 'Abierto',
    'fr-fr': 'Ouvert',
    'uk-ua': 'Відкрито',
    'de-ch': 'Geöffnet',
    'pt-br': 'Aberto',
  },
  closed: {
    'en-us': 'Closed',
    'ru-ru': 'Закрыто',
    'es-es': 'Cerrado',
    'fr-fr': 'Fermé',
    'uk-ua': 'ЗАЧИНЕНО',
    'de-ch': 'Geschlossen',
    'pt-br': 'Fechado',
  },
  leafNode: {
    'en-us': 'Leaf Node',
    'ru-ru': 'Листовой узел',
    'es-es': 'Nodo de hoja',
    'fr-fr': 'Nœud feuille',
    'uk-ua': 'Листковий вузол',
    'de-ch': 'Blattknoten',
    'pt-br': 'Nó foliar',
  },
  nodeStats: {
    comment: "Used to show tree node's direct and indirect usages",
    'en-us': '({directCount:number|formatted}, {childCount:number|formatted})',
    'ru-ru': '({directCount:number|formatted}, {childCount:number|formatted})',
    'es-es': '({directCount:number|formatted}, {childCount:number|formatted})',
    'fr-fr': '({directCount:number|formatted}, {childCount:number|formatted})',
    'uk-ua': "({directCount:number|formatted}', {childCount:number|formatted})",
    'de-ch': '({directCount:number|formatted}, {childCount:number|formatted})',
    'pt-br': '({directCount:number|formatted}, {childCount:number|formatted})',
  },
  leafNodeStats: {
    comment: "Used to show leaf tree node's direct usages",
    'en-us': '({directCount:number|formatted})',
    'ru-ru': '({directCount:number|formatted})',
    'es-es': '({directCount:number|formatted})',
    'fr-fr': '({directCount:number|formatted})',
    'uk-ua': "({directCount:number|formatted})'",
    'de-ch': '({directCount:number|formatted})',
    'pt-br': '({directCount:number|formatted})',
  },
  directCollectionObjectCount: {
    comment: 'Example: Direct Collection Object count',
    'en-us': 'Direct {collectionObjectTable:string} Count',
    'ru-ru': 'Прямой {collectionObjectTable:string} подсчет',
    'es-es': 'Recuento directo {collectionObjectTable:string}',
    'de-ch': 'Direkte {collectionObjectTable:string} Anzahl',
    'fr-fr': 'Compte direct {collectionObjectTable:string}',
    'uk-ua': 'Прямий підрахунок {collectionObjectTable:string}',
    'pt-br': 'Contagem direta {collectionObjectTable:string}',
  },
  indirectCollectionObjectCount: {
    comment: 'Example: Indirect Collection Object count',
    'en-us': 'Indirect {collectionObjectTable:string} Count',
    'ru-ru': 'Косвенный {collectionObjectTable:string} подсчет',
    'es-es': 'Recuento indirecto {collectionObjectTable:string}',
    'fr-fr': 'Compte indirect {collectionObjectTable:string}',
    'uk-ua': 'Непрямий підрахунок {collectionObjectTable:string}',
    'de-ch': 'Indirekte {collectionObjectTable:string} Anzahl',
    'pt-br': 'Contagem indireta {collectionObjectTable:string}',
  },
  editRanks: {
    'en-us': 'Edit Ranks',
    'ru-ru': 'Редактировать ранги',
    'es-es': 'Editar rangos',
    'fr-fr': 'Modifier les rangs',
    'uk-ua': 'Редагувати ранги',
    'de-ch': 'Positionen bearbeiten',
    'pt-br': 'Editar classificações',
  },
  resourceToDelete: {
    'en-us': 'This will permanently delete the following resource',
    'es-es': 'Esto eliminará permanentemente el siguiente recurso',
    'fr-fr': 'Cela supprimera définitivement la ressource suivante',
    'ru-ru': 'Это приведет к безвозвратному удалению следующего ресурса.',
    'uk-ua': 'Це призведе до остаточного видалення наступного ресурсу',
    'de-ch': 'Dadurch wird die folgende Ressource dauerhaft gelöscht',
    'pt-br': 'Isso excluirá permanentemente o seguinte recurso.',
  },
  associatedNodesOnly: {
    'en-us': 'Show only nodes with associated objects',
    'de-ch': 'Es werden nur Knoten mit zugehörigen Objekten angezeigt.',
    'es-es': 'Mostrar solo nodos con objetos asociados',
    'fr-fr': 'Afficher uniquement les nœuds avec des objets associés',
    'ru-ru': 'Отображать только узлы, связанные с объектами.',
    'uk-ua': "Показувати лише вузли з пов'язаними об'єктами",
    'pt-br': 'Mostrar apenas os nós com objetos associados.',
  },
  splitView: {
    'en-us': 'Split View',
    'de-ch': 'Geteilte Sicht',
    'es-es': 'Vista dividida',
    'fr-fr': 'Vue fractionnée',
    'ru-ru': 'Разделенный экран',
    'uk-ua': 'Розділений вид',
    'pt-br': 'Visão dividida',
  },
  horizontal: {
    'en-us': 'Horizontal',
    'de-ch': 'Horizontal',
    'es-es': 'Horizontal',
    'fr-fr': 'Horizontal',
    'ru-ru': 'Горизонтальный',
    'uk-ua': 'Горизонтальний',
    'pt-br': 'Horizontal',
  },
  vertical: {
    'en-us': 'Vertical',
    'de-ch': 'Vertikal',
    'es-es': 'Vertical',
    'fr-fr': 'Vertical',
    'ru-ru': 'Вертикальный',
    'uk-ua': 'Вертикальний',
    'pt-br': 'Vertical',
  },
  synchronize: {
    'en-us': 'Synchronize',
    'de-ch': 'Synchronisieren',
    'es-es': 'Sincronizar',
    'fr-fr': 'Synchroniser',
    'uk-ua': 'Синхронізувати',
    'ru-ru': 'Синхронизировать',
    'pt-br': 'Sincronizar',
  },
  addNewRank: {
    'en-us': 'Add New Rank',
    'de-ch': 'Neuen Rang hinzufügen',
    'es-es': 'Añadir nuevo rango',
    'fr-fr': 'Ajouter un nouveau rang',
    'ru-ru': 'Добавить новый ранг',
    'uk-ua': 'Додати новий ранг',
    'pt-br': 'Adicionar novo cargo',
  },
  chooseParentRank: {
    'en-us': 'Choose Parent Rank',
    'de-ch': 'Übergeordneten Rang auswählen',
    'es-es': 'Elija el rango de los padres',
    'fr-fr': 'Choisir le rang du parent',
    'ru-ru': 'Выберите родительский ранг',
    'uk-ua': 'Виберіть батьківський рейтинг',
    'pt-br': 'Escolha a classificação principal',
  },
  moveItems: {
    'en-us': 'Move Items',
    'de-ch': 'Gegenstände verschieben',
    'es-es': 'Mover elementos',
    'fr-fr': 'Déplacer des éléments',
    'ru-ru': 'Переместить предметы',
    'uk-ua': 'Переміщення елементів',
    'pt-br': 'Mover itens',
  },
  addTree: {
    'en-us': 'Add Tree',
    'de-ch': 'Baum hinzufügen',
    'es-es': 'Agregar árbol',
    'fr-fr': 'Ajouter un arbre',
    'pt-br': 'Adicionar árvore',
    'ru-ru': 'Добавить дерево',
    'uk-ua': 'Додати дерево',
  },
  treePicker: {
    'en-us': 'Tree Picker',
    'de-ch': 'Baumpflücker',
    'es-es': 'Selector de árboles',
    'fr-fr': "Cueilleur d'arbres",
    'pt-br': 'apanhador de árvores',
    'ru-ru': 'Сборщик деревьев',
    'uk-ua': 'Збирач дерев',
  },
  botany: {
    'en-us': 'Botany',
    'de-ch': 'Botanik',
    'es-es': 'Botánica',
    'fr-fr': 'Botanique',
    'pt-br': 'Botânica',
    'ru-ru': 'Ботаника',
    'uk-ua': 'Ботаніка',
  },
  entomology: {
    'en-us': 'Entomology',
    'de-ch': 'Entomologie',
    'es-es': 'Entomología',
    'fr-fr': 'Entomologie',
    'pt-br': 'Entomologia',
    'ru-ru': 'Энтомология',
    'uk-ua': 'Ентомологія',
  },
  herpetology: {
    'en-us': 'Herpetology',
    'de-ch': 'Herpetologie',
    'es-es': 'Herpetología',
    'fr-fr': 'Herpétologie',
    'pt-br': 'Herpetologia',
    'ru-ru': 'Герпетология',
    'uk-ua': 'Герпетологія',
  },
  ichthyology: {
    'en-us': 'Ichthyology',
    'de-ch': 'Fischkunde',
    'es-es': 'Ictiología',
    'fr-fr': 'Ichtyologie',
    'pt-br': 'Ictiologia',
    'ru-ru': 'Ихтиология',
    'uk-ua': 'Іхтіологія',
  },
  invertpaleo: {
    'en-us': 'Invertebrate Paleontology',
    'de-ch': 'Wirbellosenpaläontologie',
    'es-es': 'Paleontología de invertebrados',
    'fr-fr': 'Paléontologie des invertébrés',
    'pt-br': 'Paleontologia de Invertebrados',
    'ru-ru': 'Палеонтология беспозвоночных',
    'uk-ua': 'Палеонтологія безхребетних',
  },
  invertzoo: {
    'en-us': 'Invertebrate Zoology',
    'de-ch': 'Wirbellosenzoologie',
    'es-es': 'Zoología de invertebrados',
    'fr-fr': 'Zoologie des invertébrés',
    'pt-br': 'Zoologia de Invertebrados',
    'ru-ru': 'Зоология беспозвоночных',
    'uk-ua': 'Зоологія безхребетних',
  },
  mammalogy: {
    'en-us': 'Mammalogy',
    'de-ch': 'Mammalogie',
    'es-es': 'Mastozoología',
    'fr-fr': 'Mammalogie',
    'pt-br': 'Mamalogia',
    'ru-ru': 'Маммология',
    'uk-ua': 'Мамалогія',
  },
  ornithology: {
    'en-us': 'Ornithology',
    'de-ch': 'Vogelkunde',
    'es-es': 'Ornitología',
    'fr-fr': 'Ornithologie',
    'pt-br': 'Ornitologia',
    'ru-ru': 'Орнитология',
    'uk-ua': 'Орнітологія',
  },
  paleobot: {
    'en-us': 'Paleobotany',
    'de-ch': 'Paläobotanik',
    'es-es': 'Paleobotánica',
    'fr-fr': 'Paléobotanique',
    'pt-br': 'Paleobotânica',
    'ru-ru': 'Палеоботаника',
    'uk-ua': 'Палеоботаніка',
  },
  vascplant: {
    'en-us': 'Vascular Plants',
    'de-ch': 'Gefäßpflanzen',
    'es-es': 'Plantas vasculares',
    'fr-fr': 'Plantes vasculaires',
    'pt-br': 'Plantas Vasculares',
    'ru-ru': 'Сосудистые растения',
    'uk-ua': 'Судинні рослини',
  },
  vertpaleo: {
    'en-us': 'Vertebrate Paleontology',
    'de-ch': 'Wirbeltierpaläontologie',
    'es-es': 'Paleontología de vertebrados',
    'fr-fr': 'Paléontologie des vertébrés',
    'pt-br': 'Paleontologia de Vertebrados',
    'ru-ru': 'Палеонтология позвоночных',
    'uk-ua': 'Палеонтологія хребетних',
  },
  emptyTree: {
    'en-us': 'Empty Tree',
    'de-ch': 'Leerer Baum',
    'es-es': 'Árbol vacío',
    'fr-fr': 'Arbre vide',
    'pt-br': 'Árvore Vazia',
    'ru-ru': 'Пустое дерево',
    'uk-ua': 'Порожнє дерево',
  },
  minerals: {
    'en-us': 'Minerals',
    'de-ch': 'Mineralien',
    'es-es': 'Minerales',
    'fr-fr': 'Minéraux',
    'pt-br': 'Minerais',
    'ru-ru': 'Минералы',
    'uk-ua': 'Мінерали',
  },
  rocks: {
    'en-us': 'Rocks',
    'de-ch': 'Felsen',
    'es-es': 'Rocas',
    'fr-fr': 'Roches',
    'pt-br': 'Rochas',
    'ru-ru': 'Камни',
    'uk-ua': 'Скелі',
  },
  meteorites: {
    'en-us': 'Meteorites',
    'de-ch': 'Meteoriten',
    'es-es': 'Meteoritos',
    'fr-fr': 'Météorites',
    'pt-br': 'Meteoritos',
    'ru-ru': 'Метеориты',
    'uk-ua': 'Метеорити',
  },
  emptyTrees: {
    'en-us': 'Empty Trees',
  },
  populatedTrees: {
    'en-us': 'Populated Trees',
  },
  defaultTreeCreationStarted: {
    'en-us': 'Default Tree Creation Started',
  },
  defaultTreeCreationStartedDescription: {
    'en-us':
      'The tree will be created in the background. You will be notified once it is completed.',
  },
  defaultTreeTaskStarted: {
    'en-us': 'The default tree creation process has started.',
  },
  defaultTreeTaskFailed: {
    'en-us': 'Default tree creation failed.',
  },
  defaultTreeTaskCancelled: {
    'en-us': 'Default tree creation was cancelled.',
  },
  defaultTreeTaskCompleted: {
    'en-us': 'Default tree creation completed successfully.',
  },
  defaultTreeCreationProgress: {
    comment: 'E.x, Creating tree record 999/1,000',
    'en-us':
      'Creating tree record {current:number|formatted}/{total:number|formatted}',
  },
  defaultTreeCreationLoadingMessage: {
    'en-us': 'Default tree creation is in progress, please wait...',
  },
  missingRanks: {
    'en-us': 'Missing Ranks',
  },
  missingRanksDescription: {
    'en-us':
      'The populated tree you selected to download contains records in ranks that are missing from your tree.',
  },
  createMissingRanks: {
    'en-us': 'Should the missing ranks be created?',
  },
  source: {
    'en-us': 'Source',
  },
  initializeEmptyTree: {
    'en-us': 'Initialize an empty tree',
  },
  treeManagement: {
    'en-us': 'Tree Management',
    'de-ch': 'Baumpflege',
    'es-es': 'Manejo de árboles',
    'fr-fr': 'Gestion des arbres',
    'pt-br': 'Gestão de árvores',
    'ru-ru': 'Управление деревьями',
    'uk-ua': 'Управління деревами',
  },
  synonymizedNodes: {
    'en-us': 'Expand Synonym Behavior',
    'de-ch': 'Synonymverhalten erweitern',
    'es-es': 'Expandir el comportamiento de los sinónimos',
    'fr-fr': 'Développer le comportement des synonymes',
    'pt-br': 'Expandir o comportamento dos sinônimos',
    'ru-ru': 'Развернуть поведение синонимов',
    'uk-ua': 'Розгорнути поведінку синонімів',
  },
  synonymizedNodesDescription: {
    'en-us':
      'If enabled, this allows users to add children to synonymized parents and to synonymize a node with children.',
    'de-ch':
      'Ist diese Funktion aktiviert, können Benutzer Kindknoten zu synonymisierten Elternknoten hinzufügen und einen Knoten mit Kindknoten synonymisieren.',
    'es-es':
      'Si está habilitado, esto permite a los usuarios agregar hijos a padres sinonimizados y sinonimizar un nodo con hijos.',
    'fr-fr':
      "Si cette option est activée, elle permet aux utilisateurs d'ajouter des enfants à des parents synonymisés et de synonymiser un nœud avec ses enfants.",
    'pt-br':
      'Se ativada, essa opção permite que os usuários adicionem filhos a nós pais sinonimizados e sinonimizem um nó com seus filhos.',
    'ru-ru':
      'Если эта функция включена, она позволяет пользователям добавлять дочерние элементы к синонимизированным родительским элементам и синонимизировать узел с дочерними элементами.',
    'uk-ua':
      'Якщо це ввімкнено, користувачі можуть додавати дочірні елементи до синонімізованих батьківських елементів та синонімізувати вузол з дочірніми елементами.',
  },
  utf8EncodingWarning: {
    'en-us':
      'This tree data cannot be imported in your database because it contains UTF-8 characters and this database uses {encoding:string}',
  },
  resolveEncoding: {
    'en-us': 'How to resolve:',
  },
} as const);
